/**
 * SyncBanner — shown while a sync job is running.
 * Rendered in DashboardLayout directly below the app header (same band as the
 * mobile Getting started widget). Hidden on /data-sync/:jobId (full progress there).
 */
import React from 'react';
import { Box, Typography, Button, IconButton, LinearProgress, Stack, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import { SyncJobContext } from './SyncJobProvider';
import type { SyncStep } from './SyncJobProvider';

// Matches the same logic as DataSyncPage
function parseUtcDate(input: string): Date {
  if (input.includes('T') && !input.endsWith('Z') && !input.includes('+') && !input.split('T')[1]?.includes('-')) {
    return new Date(input + 'Z');
  }
  return new Date(input);
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function findRunningStep(steps: SyncStep[]): SyncStep | null {
  for (const step of steps) {
    if (step.status === 'running') return step;
    if (step.substeps) {
      const sub = findRunningStep(step.substeps);
      if (sub) return sub;
    }
  }
  return null;
}

function normaliseLabel(label: string): string {
  if (label === 'Build Data Relationships') return 'Build Data Model';
  return label;
}

interface SyncBannerProps {
  ml?: number | { xs?: number; md?: number };
  mr?: number | { xs?: number; md?: number };
}

export default function SyncBanner({ ml, mr }: SyncBannerProps) {
  const { runningJob, dismissJob, isDismissed } = React.useContext(SyncJobContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Live elapsed timer
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    if (!runningJob) { setElapsed(0); return; }
    const ref = runningJob.started_at || runningJob.created_at;
    const tick = () => setElapsed(Date.now() - parseUtcDate(ref).getTime());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [runningJob]);

  // Don't show on the job detail page - user already sees full progress there
  const isOnJobPage = /^\/data-sync\/.+/.test(location.pathname);

  if (!runningJob || isDismissed(runningJob.job_id) || isOnJobPage) return null;

  const steps = runningJob.progress?.steps ?? [];
  const completedCount = steps.filter((s: SyncStep) => s.status === 'completed').length;
  const totalSteps = steps.length || 3;
  const activeStep = findRunningStep(steps);
  const stepLabel = activeStep ? normaliseLabel(activeStep.label) : (runningJob.status === 'pending' ? 'Starting…' : 'Running…');

  const connectorCount = runningJob.connector_ids.length;
  const progressFraction = totalSteps > 0 ? completedCount / totalSteps : null;

  // All visual values come from design tokens. Tone driven by `semantic.warning`.
  const tokens = theme.tokens;
  const titleColor = tokens.text.primary;
  const bodyColor = tokens.text.secondary;
  const mutedColor = tokens.text.tertiary;
  const accent = tokens.semantic.warning;
  const accentSoft = tokens.semantic.warningSoft;
  const chipLabel = `Step ${Math.min(completedCount + 1, totalSteps)} of ${totalSteps}`;
  const chipSxBase = {
    fontSize: '0.75rem',
    fontWeight: 600,
    bgcolor: accentSoft,
    color: accent,
    border: `1px solid ${tokens.border.subtle}`,
    '& .MuiChip-label': { px: 0.875 },
  };

  const stepChipMobile =
    totalSteps > 0 ? (
      <Chip label={chipLabel} size="small" sx={{ ...chipSxBase, height: 22 }} />
    ) : null;

  const stepChip =
    totalSteps > 0 ? (
      <Chip
        label={chipLabel}
        size="small"
        sx={{ ...chipSxBase, height: 22 }}
      />
    ) : null;

  const progressButtonSx = {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: accent,
    borderRadius: `${theme.ds.radius.sm}px`,
    textTransform: 'none' as const,
    '&:hover': { bgcolor: accentSoft },
  };

  const dismissSx = {
    color: mutedColor,
    '&:hover': { color: titleColor, bgcolor: tokens.surface.sunken },
  } as const;

  const pulseDot = (
    <Box sx={{ flexShrink: 0, position: 'relative', width: 8, height: 8 }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: '50%',
        bgcolor: accent,
        position: 'absolute',
        '@keyframes ping': {
          '0%':       { transform: 'scale(1)',   opacity: 0.7 },
          '75%, 100%': { transform: 'scale(2.2)', opacity: 0 },
        },
        animation: 'ping 1.4s ease-in-out infinite',
      }} />
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, position: 'absolute' }} />
    </Box>
  );

  const dismissBtnMobile = (
    <IconButton
      size="small"
      onClick={() => dismissJob(runningJob.job_id)}
      sx={{ ...dismissSx, p: '10px', flexShrink: 0 }}
      aria-label="Dismiss banner"
    >
      <CloseIcon sx={{ fontSize: 20 }} />
    </IconButton>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: ml ? (typeof ml === 'number' ? `${ml}px` : { xs: `${ml.xs ?? 0}px`, md: `${ml.md ?? 0}px` }) : undefined,
        mr: mr ? (typeof mr === 'number' ? `${mr}px` : { xs: `${mr.xs ?? 0}px`, md: `${mr.md ?? 0}px` }) : undefined,
        bgcolor: accentSoft,
        borderBottom: `1px solid ${tokens.border.subtle}`,
      }}
    >
      {/* Mobile: stacked title, step row, meta, full-width CTA */}
      <Box
        sx={{
          display: { xs: 'block', sm: 'none' },
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
        }}
      >
        <Stack spacing={1.25}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              component="div"
              fontWeight={700}
              sx={{ fontSize: '0.8125rem', lineHeight: 1.35, color: titleColor, pr: 0.5 }}
            >
              Build Data Model is running
            </Typography>
            {dismissBtnMobile}
          </Box>
          <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, rowGap: 0.75 }}>
            {pulseDot}
            {stepChipMobile}
            <Typography sx={{ fontSize: '0.8125rem', color: bodyColor, lineHeight: 1.35, fontWeight: 600 }}>
              {stepLabel}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: mutedColor, display: 'block' }}>
            {connectorCount} connector{connectorCount !== 1 ? 's' : ''}
            <Box component="span" sx={{ mx: 0.75, opacity: 0.7 }}>·</Box>
            {formatElapsed(elapsed)}
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            size="medium"
            startIcon={<AccountTreeRounded sx={{ fontSize: '18px !important' }} />}
            onClick={() => navigate(`/data-sync/${runningJob.job_id}`)}
            sx={{
              ...progressButtonSx,
              py: 1.125,
              borderColor: tokens.border.default,
              '&:hover': {
                borderColor: accent,
                bgcolor: accentSoft,
              },
            }}
          >
            View progress
          </Button>
        </Stack>
      </Box>

      {/* sm+: single horizontal row */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          px: { sm: 2, md: 3 },
          py: 0.75,
          alignItems: 'center',
          gap: 1.5,
          minHeight: 40,
        }}
      >
        {pulseDot}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8125rem', color: titleColor, whiteSpace: 'nowrap' }}>
            Build Data Model is running
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>·</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>
            {stepLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: mutedColor, whiteSpace: 'nowrap' }}>·</Typography>
          {stepChip}
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: mutedColor, whiteSpace: 'nowrap' }}>·</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>
            {connectorCount} connector{connectorCount !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: mutedColor, whiteSpace: 'nowrap' }}>·</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>
            {formatElapsed(elapsed)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            startIcon={<AccountTreeRounded sx={{ fontSize: '14px !important' }} />}
            onClick={() => navigate(`/data-sync/${runningJob.job_id}`)}
            sx={{ ...progressButtonSx, px: 1 }}
          >
            View progress
          </Button>
          <IconButton
            size="small"
            onClick={() => dismissJob(runningJob.job_id)}
            sx={dismissSx}
            aria-label="Dismiss banner"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Thin progress bar at the very bottom edge */}
      <LinearProgress
        variant={progressFraction !== null ? 'determinate' : 'indeterminate'}
        value={progressFraction !== null ? progressFraction * 100 : undefined}
        sx={{
          height: 2,
          bgcolor: tokens.border.subtle,
          '& .MuiLinearProgress-bar': { bgcolor: accent },
        }}
      />
    </Box>
  );
}
